import type { FunctionComponent } from 'react';
import { useSuburbPageQuery } from '../../graphql/useSuburbPageQuery';
import { useSuburbPageModalContext } from '../../contexts/SuburbPageModalContext/SuburbPageModalContext';
import { NearbyChildcareCentresBrick } from '../../../components/NearbyChildcareCentresBrick/NearbyChildcareCentresBrick';
import { ChildcareAccordionTable } from '../../../components/ChildcareAccordionTable/ChildcareAccordionTable';
import { ChildcareCentre } from '../../../components/ChildcareAccordionTable/types';
import { mapApiBadgeVariantToBadgeVariant } from '../../../app-kit/helpers/mapApiBadgeVariantToBadgeVariant';
import { mapApiWeblinkVariantToLinkVariant } from '../../../app-kit/helpers/mapApiWeblinkVariantToLinkVariant';
import { ResultOf } from 'gql.tada';
import { suburbPageQuery } from '../../graphql/suburbPageQuery';

type ApiChildcareCentre = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<ResultOf<typeof suburbPageQuery>['suburbPageBySlug']>['nearbyChildcareCentres']
    >['centres']
  >[number]
>;

const mapApiChildcareCentreToChildcareCentreProp = (
  childcareCentre: ApiChildcareCentre,
): ChildcareCentre => {
  return {
    isDefaultOpen: childcareCentre.isDefaultOpen,
    tracking: childcareCentre.tracking,
    ...childcareCentre.centre,
    vacancyBadge: {
      text: childcareCentre.centre.vacancyBadge.text,
      variant: mapApiBadgeVariantToBadgeVariant(childcareCentre.centre.vacancyBadge.variant),
    },
    webLinks: childcareCentre.centre.webLinks
      ? childcareCentre.centre.webLinks.map((webLink) => ({
          ...webLink,
          variant: mapApiWeblinkVariantToLinkVariant(webLink.variant),
        }))
      : [],
  };
};

export const SuburbPageNearbyChildcareCentresBrickWithData: FunctionComponent = () => {
  const suburbPage = useSuburbPageQuery()?.data?.suburbPageBySlug;
  const { displayModal } = useSuburbPageModalContext();

  if (
    !suburbPage ||
    !('nearbyChildcareCentres' in suburbPage) ||
    !suburbPage.nearbyChildcareCentres ||
    !suburbPage.nearbyChildcareCentres.centres
  )
    return null;

  const { title, subtitle, centreFactHeader, centres } = suburbPage.nearbyChildcareCentres;

  return (
    <NearbyChildcareCentresBrick
      title={title}
      subtitle={subtitle}
      pageSource="suburb_page"
      displayModal={displayModal}
    >
      <ChildcareAccordionTable
        childcareFactHeader={centreFactHeader}
        childcareCentres={centres.map(mapApiChildcareCentreToChildcareCentreProp)}
      />
    </NearbyChildcareCentresBrick>
  );
};
